import { Button, Checkbox, CheckboxGroup, Divider, Modal, Spinner, TextArea, TextField } from '@nike/eds'
import { ConfirmationModal } from 'components/confirmation-modal'
import { useState } from 'react'
import { useAppSelector } from 'redux/store'
import { type GradingInstruction } from 'types/Instruction'

import './GradingInstructionEditor.scss'
import { AuthRole } from 'auth/const'

interface GradingInstructionEditorProps {
  gradingInstruction: GradingInstruction
  mode: 'Create' | 'Edit'
  onSave: (gradingInstruction: GradingInstruction) => void
  onDismiss: () => void
  onLoading: boolean
}

export const GradingInstructionEditor = ({ gradingInstruction, mode, onSave, onDismiss, onLoading }: GradingInstructionEditorProps) => {
  const user = useAppSelector((state) => state.globalState.user)

  const initialErrors = { style: '', color: '', description: '', instruction: '' }
  const [errors, setErrors] = useState(initialErrors)
  const [instruction, setInstruction] = useState<GradingInstruction>(gradingInstruction)
  const [confirmationOpen, setConfirmationOpen] = useState(false)

  const STYLE_REGEX = /^[a-zA-Z0-9]{6}$/
  const COLOR_REGEX = /^([a-zA-Z0-9]{3}|)$/
  const DESCRIPTION_MAX_LENGTH = 255
  const INSTRUCTION_MAX_LENGTH = 255

  const disableForm = user?.hasSpecific(AuthRole.SPECIAL_INSTRUCTION) ? false : true

  const save = () => {
    if (isValid(instruction)) {
      if (instruction.acceptableGrades.length === 0) {
        setConfirmationOpen(true)
        return
      }
      onSave(instruction)
    }
  }

  const isValid = (instruction: GradingInstruction): boolean => {
    const errors = initialErrors
    errors.style = STYLE_REGEX.test(instruction.style) ? '' : '6 digits/letters'
    errors.color = COLOR_REGEX.test(instruction.color) ? '' : 'Empty or 3 digits/letters'
    errors.description = instruction.description.length > DESCRIPTION_MAX_LENGTH ? 'This field cannot be larger than 255 characters' : ''
    if (instruction.instruction === '') {
      errors.instruction = 'This field is required'
    } else if (instruction.instruction.length > INSTRUCTION_MAX_LENGTH) {
      errors.instruction = 'This field cannot be larger than 255 characters'
    }
    setErrors(errors)
    return Object.values(errors).find(error => error) === undefined
  }

  const handleChange = (field: string, value: string) => {
    setErrors(prevState => ({ ...prevState, [field]: '' }))
    setInstruction(prevState => ({
      ...prevState,
      [field]: value
    }))
  }

  const handleCheckBox = async (id: string, checked: boolean) => {
    let grade: string | undefined
    switch (id) {
      case 'aGrade': grade = 'A'; break
      case 'bGrade': grade = 'B'; break
      case 'cGrade': grade = 'C'; break
      case 'sGrade': grade = 'S'; break
      case 'hash': grade = 'HASH'; break
    }
    if (checked) {
      setInstruction(prevState => ({
        ...prevState,
        acceptableGrades: [...prevState.acceptableGrades, grade!]
      }))
    } else {
      setInstruction(prevState => ({
        ...prevState,
        acceptableGrades: prevState.acceptableGrades.filter(g => g !== grade!)
      }))
    }
  }

  const footerSlot = () => {
    if (onLoading) {
      return <Button size="medium" variant="primary" disabled><Spinner size="medium" /></Button>
    }
    if (disableForm) {
      return <Button size="medium" variant="primary" onClick={onDismiss}>Close</Button>
    } else {
      return <Button size="medium" variant="primary" onClick={save}>Save</Button>
    }
  }

  return (
    <Modal
      isOpen={true}
      headerSlot={mode === 'Create' ? 'Create grading instruction' : 'Edit grading instruction'}
      footerSlot={<div>{footerSlot()}</div>}
      onDismiss={onDismiss}
      hideFade={true}>
      <div className="instruction-modal">
        <div className="product-code">
          <div className="style w-1/5">
            <TextField
              id="style"
              type="text"
              label="Style"
              disabled={disableForm}
              value={instruction.style}
              maxChars={6}
              onChange={(e) => {
                handleChange('style', e.target.value)
              }}
              hasErrors={errors.style !== ''}
              errorMessage={errors.style}
            />
          </div>
          <div className="w-1/5">
            <TextField
              id="color"
              type="text"
              label="Color"
              disabled={disableForm}
              value={instruction.color}
              maxChars={3}
              onChange={(e) => {
                handleChange('color', e.target.value)
              }}
              hasErrors={errors.color !== ''}
              errorMessage={errors.color}
            />
          </div>
        </div>

        <Divider />

        <TextArea
          showCount={true}
          id="description"
          label="Description"
          disabled={disableForm}
          subtitle="Text to provide context and legibility within the admin UI"
          value={instruction.description}
          maxChars={255}
          minRows={2}
          onChange={(e) => { handleChange('description', e.target.value) }}
          hasErrors={errors.description !== ''}
          errorMessage={errors.description}
        />
        <TextArea
          showCount={true}
          id="instruction"
          label="Special Instruction"
          disabled={disableForm}
          subtitle="What will actually displayed during unit-handling"
          value={instruction.instruction}
          maxChars={255}
          minRows={2}
          onChange={(e) => { handleChange('instruction', e.target.value) }}
          hasErrors={errors.instruction !== ''}
          errorMessage={errors.instruction}
        />
        <CheckboxGroup
          id="availableGrades"
          label="Available grades"
          name="availableGrades"
          orientation='horizontal'
          className={'mb-6'}
          onChange={(e) => { handleCheckBox(e.target.id, e.target.checked) }}
        >
          <Checkbox label="A" id="aGrade" checked={instruction.acceptableGrades.includes('A')} disabled={disableForm} />
          <Checkbox label="B" id="bGrade" checked={instruction.acceptableGrades.includes('B')} disabled={disableForm} />
          <Checkbox label="C" id="cGrade" checked={instruction.acceptableGrades.includes('C')} disabled={disableForm} />
          <Checkbox label="S" id="sGrade" checked={instruction.acceptableGrades.includes('S')} disabled={disableForm} />
          <Checkbox label="Hash" id="hash" checked={instruction.acceptableGrades.includes('HASH')} disabled={disableForm} />
        </CheckboxGroup>
      </div>
      {confirmationOpen &&
        <ConfirmationModal
          title="No grade selected"
          message="You have not selected any grades. This means that this material cannot be processed during unit handling. Are you sure you want to proceed?"
          onCancel={() => {
            setConfirmationOpen(false)
          }}
          onTrigger={() => {
            setConfirmationOpen(false)
            if (isValid(instruction)) {
              onSave(instruction)
            }
          }}
          confirmText={'Save'}
        />
      }
    </Modal>
  )
}
